import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { of, switchMap, throwError, timer } from 'rxjs';

import { ModalComponent } from '../../shared/modal/modal';
import { placeRefusal } from '../../shared/modal/modal-refusal';

/** `detail` as the API sends it: a map names fields, a string refuses the whole act. */
export type ModalDemoRefusal = Record<string, string[]> | string | null;

export interface ModalDemoData {
  /** The refusal the first save gets, as the API words it. */
  readonly refusal: ModalDemoRefusal;
}

/** The style guide's working example of `<app-modal>`. The save is simulated. */
@Component({
  selector: 'app-style-guide-modal-demo',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ModalComponent,
  ],
  template: `
    <app-modal
      heading="Update your details"
      description="We use these to reach you about your applications."
      submitLabel="Save changes"
      [formGroup]="form"
      [pending]="pending()"
      [error]="error()"
      (submitted)="save()"
    >
      <div class="modal-demo__fields">
        <mat-form-field appearance="outline">
          <mat-label>Full name</mat-label>
          <input matInput formControlName="name" autocomplete="name" />
          <mat-error>Enter your full name.</mat-error>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Mobile number</mat-label>
          <input matInput formControlName="mobile" type="tel" autocomplete="tel" />
          <mat-hint>For example, 082 555 0134.</mat-hint>
          @if (form.controls.mobile.hasError('serverError')) {
            <mat-error>{{ form.controls.mobile.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Enter your mobile number.</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Best way to reach you</mat-label>
          <mat-select formControlName="contact">
            <mat-option value="EMAIL">Email</mat-option>
            <mat-option value="PHONE">Phone</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </app-modal>
  `,
  styles: `
    .modal-demo__fields {
      display: grid;
      gap: var(--space-2);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StyleGuideModalDemoComponent {
  private readonly data = inject<ModalDemoData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<StyleGuideModalDemoComponent, boolean>>(MatDialogRef);

  protected readonly form = inject(FormBuilder).nonNullable.group({
    name: ['Thabo Mokoena', Validators.required],
    mobile: ['082 555 0134', Validators.required],
    contact: ['EMAIL'],
  });
  protected readonly pending = signal(false);
  protected readonly error = signal<string | null>(null);
  private nextRefusal = this.data.refusal;

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.pending.set(true);
    this.error.set(null);
    this.simulateWrite().subscribe({
      next: () => this.dialogRef.close(true),
      error: (err: unknown) => {
        this.pending.set(false);
        this.error.set(placeRefusal(this.form, err, 'Could not save. Please try again.'));
      },
    });
  }

  private simulateWrite() {
    const detail = this.nextRefusal;
    this.nextRefusal = null;
    return timer(800).pipe(
      switchMap(() =>
        detail
          ? throwError(() => new HttpErrorResponse({ status: 400, error: { detail } }))
          : of(true),
      ),
    );
  }
}
